import { languageName } from '@boViews/shared/LanguageTabs/languageName';
import { compactI18n, displayText, parseI18n } from '@src/i18n';

import { highlightSegments } from './targetSearchRules';

// Number of results the test search shows: one more is asked for, to tell whether there are others.
export const RESULT_LIMIT = 20;

// Splits a text around the words of the search (case and accents ignored), to show them in bold, as the API
// searches each word on its own. The longest words are marked first, so that a word within another one does
// not split it.
export const matchParts = (text, search) =>
    (search ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)
        .reduce(
            (parts, word) => parts.flatMap((part) => (part.match ? [part] : highlightSegments(part.text, word))),
            [{ text: text ?? '', match: false }],
        )
        .filter((part) => part.text !== '');

// Title of a test to display (its i18n JSON string read in French, else in the first language that has one), or ''
// when no language has one.
export const testTitle = (test) => displayText(compactI18n(parseI18n(test.title)));

// The test of `reference` among `tests`, or undefined when it is not one of them.
export const collectionEntry = (tests, reference) => tests.find((test) => test.reference === reference);

// Full name, in `locale`, of the catalogue language `languageId` (`languagesOptions`: its id labelled by its
// code), or null when it is missing or unknown.
export const languageLabel = (languagesOptions, languageId, locale) => {
    const code = languagesOptions.find((option) => option.value === languageId)?.label;
    return code ? languageName(code, locale) : null;
};

// Tests ordered by reference, as the API lists them.
export const sortedTests = (tests) => [...tests].sort((a, b) => a.reference.localeCompare(b.reference, 'en'));

// Rank of each status in the lifecycle of a test, which the status column sorts by.
const STATUS_RANKS = { DRAFT: 0, ONLINE: 1, ARCHIVED: 2 };

// Value of a test that each sortable column sorts by, as displayed: null when the test lacks it.
const SORT_VALUES = {
    reference: (test) => test.reference,
    name: (test) => testTitle(test) || null,
    language: (test, { languagesOptions, locale }) => languageLabel(languagesOptions, test.languageId, locale),
    status: (test) => STATUS_RANKS[test.status] ?? null,
};

export const SORTABLE_COLUMNS = Object.keys(SORT_VALUES);

/**
 * Tests sorted by the column of `sortBy`, one of SORTABLE_COLUMNS, prefixed with "-" for the descending order: the
 * displayed title for 'name', the displayed language name for 'language', the lifecycle (draft, online, archived)
 * for 'status'. A test lacking the value comes last in both directions; ties are ordered by reference. Texts are
 * compared in `locale` regardless of case and accents, their numbers by value.
 */
export const sortTestsBy = (tests, sortBy, { languagesOptions = [], locale = 'en' } = {}) => {
    const descending = sortBy.startsWith('-');
    const valueOf = SORT_VALUES[sortBy.replace(/^-/, '')];
    const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
    const byValue = (a, b) => {
        if (a.value === null || b.value === null) return Number(a.value === null) - Number(b.value === null);
        const order = typeof a.value === 'number' ? a.value - b.value : collator.compare(a.value, b.value);
        return descending ? -order : order;
    };
    return tests
        .map((test) => ({ test, value: valueOf(test, { languagesOptions, locale }) }))
        .sort((a, b) => byValue(a, b) || collator.compare(a.test.reference, b.test.reference))
        .map(({ test }) => test);
};

// The collection with `test` among its tests, at its place by reference: a test already shown is replaced.
export const withAddedTest = (collection, test) => ({
    ...collection,
    tests: sortedTests([...collection.tests.filter((other) => other.reference !== test.reference), test]),
});

export const withoutTest = (collection, reference) => ({
    ...collection,
    tests: collection.tests.filter((test) => test.reference !== reference),
});

// Toast of a failed addition: a 409 means the test was already in the collection, which only needs a warning.
export const addTestErrorToast = (t, error) =>
    error.status === 409
        ? { type: 'warning', text: t('This test is already in the collection.') }
        : { type: 'error', text: t('An error occurred while adding. Please try again.') };

// Toast of a failed removal: a 404 means the test had already left the collection, which only needs a warning.
export const removeTestErrorToast = (t, error, reference) =>
    error.status === 404
        ? { type: 'warning', text: t('{{reference}} was no longer in the collection.', { reference }) }
        : { type: 'error', text: t('An error occurred while saving. Please try again.') };
